import React from 'react';
import { Clock3, Trophy, Umbrella, ShieldCheck } from 'lucide-react';
import { protectionLabel } from './VacationDateMenu';
import { formatCompactInteger } from '../utils/numberFormat';
import { t } from '../i18n';
import './DayCell.css';

/**
 * DayCell component displays a single day in the calendar grid
 * 
 * Requirements:
 * - 5.2: Display total practicing time for practice days
 * - 5.3: Display total gained score for practice days
 * - 5.4: Format day cell data as "[time] [score]"
 * - 5.5: Visually distinguish practiced days from non-practiced days
 * - 5.6: Display empty or minimally styled cell for non-practiced days
 * 
 * @param {Object} props
 * @param {number} props.dayNumber - Day number (1-31)
 * @param {Object|null} props.stats - Daily statistics or null if no practice
 * @param {number} props.stats.totalSeconds - Total practice time in seconds
 * @param {number} props.stats.totalScoreGained - Total gained score
 * @param {boolean} props.isToday - Whether this is today's date
 * @param {boolean} props.isCurrentMonth - Whether this day belongs to the current month
 */
function DayCell({ dayNumber, stats, isToday = false, isCurrentMonth = true, date, protection, onSelect, isSelected = false, hasStudied }) {
  const hasStats = stats && stats.totalSeconds > 0;
  const hasPractice = hasStudied ?? hasStats;
  
  // Build CSS classes
  const classes = [
    'day-cell',
    hasPractice ? 'day-cell-practiced' : 'day-cell-empty',
    isToday ? 'day-cell-today' : '',
    !isCurrentMonth ? 'day-cell-other-month' : ''
  ].filter(Boolean).join(' ');

  const Cell = onSelect ? 'button' : 'div';
  return (
    <Cell className={classes} {...(onSelect ? { type: 'button', onClick: () => onSelect(date), 'data-calendar-date': date, 'aria-label': t('streakProtection.selectDate', undefined, { date }), 'aria-expanded': isSelected, 'aria-controls': isSelected ? `vacation-menu-${date}` : undefined } : {})}>
      <span className="day-number" title={hasStudied ? t('streakProtection.studyDay') : undefined}>{dayNumber}</span>
      {protection && <span className={`day-protection day-protection-${protection.type}`} role="img" aria-label={protectionLabel(protection)} title={protectionLabel(protection)}>
        {protection.type === 'superpass' ? <ShieldCheck size={16} aria-hidden="true" /> : <Umbrella size={16} aria-hidden="true" />}
      </span>}
      {hasStats && (
        <span className="day-stats" title={t('accountActivity.calendar.dayStats', undefined, {
          time: formatTime(stats.totalSeconds), score: formatCompactInteger(stats.totalScoreGained),
        })} aria-label={t('accountActivity.calendar.dayStats', undefined, {
          time: formatTime(stats.totalSeconds), score: formatCompactInteger(stats.totalScoreGained),
        })}>
          <span className="day-stat-time"><Clock3 size={11} aria-hidden="true" /><span className="day-stat-value"><span>{Math.round(Math.max(0, Number(stats.totalSeconds) || 0) / 60)}</span><span className="day-time-unit">{t('accountActivity.stats.minutesValue', undefined, { count: '' })}</span></span></span>
          <span className="day-stat-score"><Trophy size={11} aria-hidden="true" /><span>{formatCompactInteger(stats.totalScoreGained)}</span></span>
        </span>
      )}
    </Cell>
  );
}

/**
 * Format seconds into human-readable time string
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time in total minutes (e.g., "30m", "90m")
 */
function formatTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  return t('accountActivity.stats.minutesValue', undefined, { count: Math.round(safeSeconds / 60) });
}

export default DayCell;
export { formatTime };
